/**
 * SpellManager - Presenter Component
 *
 * Pure presenter that renders the spell editing UI using EditableListManager.
 * Receives the global spells list from SpellManagerContainer and the
 * spell items + onChange handler from the parent (CharacterSheetTabs).
 * Contains ZERO store access.
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { AutoComplete, Checkbox } from 'antd';
import type { PartySpell, Spell } from '../../types/types';
import EditableListManager from './EditableListManager';
import styles from './EditableListManager.module.css';

/**
 * Props passed through from the parent (CharacterSheetTabs) that are not
 * sourced from the store by SpellManagerContainer.
 */
export interface SpellManagerPassthroughProps {
  items: PartySpell[];
  onChange: (newItems: PartySpell[] | ((prevItems: PartySpell[]) => PartySpell[])) => void;
}

/**
 * Full props interface including data from the Container.
 */
export interface SpellManagerProps extends SpellManagerPassthroughProps {
  allSpells: Spell[];
}

const SpellManager: React.FC<SpellManagerProps> = ({ items, onChange, allSpells }) => {
  const spellOptions = (allSpells ?? []).map(spell => ({
    value: spell.name,
    label: `${spell.name} (Lvl ${spell.level})`,
    spell, // Attach the full spell object
  }));

  return (
    <EditableListManager<PartySpell>
      items={items}
      onChange={onChange}
      defaultNewItem={() => ({
        prepared: false,
        partyMemberId: 0,
        updatedAt: new Date(),
        spellId: 0,
        spell: { id: 0, name: '', level: 0 } as Spell,
      })}
      addText="Add Spell"
      renderItemFields={(item, index, handleItemChange) => (
        <div className={styles.itemFields}>
          <AutoComplete
            placeholder="Spell Name"
            value={item.spell.name}
            options={spellOptions}
            style={{ flexGrow: 1 }}
            onSelect={(_value, option) => {
              handleItemChange(index, { spell: option.spell });
            }}
            onChange={(value) => {
              handleItemChange(index, { spell: { ...item.spell, name: value } });
            }}
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
          />
          <Checkbox checked={item.prepared} onChange={(e) => handleItemChange(index, { prepared: e.target.checked })}>
            Prepared
          </Checkbox>
        </div>
      )}
    />
  );
};

export default SpellManager;
